import type { Action, AppAbility, Subject } from '@mdvip/shared/types/ability';
import type { ComponentType } from 'react';
import { AllActiveMembersView } from '@/components/member-views/views/AllActiveMembersView';
import { IppeCalculationMembersView } from '@/components/member-views/views/IppeCalculationMembersView';
import { OutstandingAwpMembersView } from '@/components/member-views/views/OutstandingAwpMembersView';
import { PastDueMembersView } from '@/components/member-views/views/PastDueMembersView';
import { RecommendedOutreachMembersView } from '@/components/member-views/views/RecommendedOutreachMembersView';
import { ScholarshipMembersView } from '@/components/member-views/views/ScholarshipMembersView';
import { SuspendedMembersView } from '@/components/member-views/views/SuspendedMembersView';
import { UpcomingRenewalMembersView } from '@/components/member-views/views/UpcomingRenewalMembersView';
import type { ProvidersKey } from './memberDataset.types';

/** Every Member Directory view, in the design's order. Each key is also the view's `?view=` value. */
export const DATASET_KEYS = [
  'all-active',
  'new',
  'past-due',
  'reinstated',
  'suspended',
  'pending-awp',
  'outstanding-awp',
  'scholarship',
  'cancelled',
  'future-cancel',
  'outreach',
  'renewals',
  'past-patients',
  'ippe',
] as const;

export type DatasetKey = (typeof DATASET_KEYS)[number];

export interface ViewEntry {
  key: DatasetKey;
  labelKey: ProvidersKey;
  /** False until the view's own story ships; the view select lists it as "Coming soon". */
  available: boolean;
  /** The component that renders this view. Absent until the view is built. */
  component?: ComponentType;
  /** An extra permission beyond the section's own `read Member` gate, for a view that needs one. */
  access?: [Action, Subject];
}

/** The view select's options. Only the views with a `component` are built so far. */
export const VIEWS: readonly ViewEntry[] = [
  { key: 'all-active', labelKey: 'memberViews.views.allActive', available: true, component: AllActiveMembersView },
  { key: 'new', labelKey: 'memberViews.views.new', available: false },
  { key: 'past-due', labelKey: 'memberViews.views.pastDue', available: true, component: PastDueMembersView },
  { key: 'reinstated', labelKey: 'memberViews.views.reinstated', available: false },
  { key: 'suspended', labelKey: 'memberViews.views.suspended', available: true, component: SuspendedMembersView },
  { key: 'pending-awp', labelKey: 'memberViews.views.pendingAwp', available: false },
  {
    key: 'outstanding-awp',
    labelKey: 'memberViews.views.outstandingAwp',
    available: true,
    component: OutstandingAwpMembersView,
  },
  {
    key: 'scholarship',
    labelKey: 'memberViews.views.scholarship',
    available: true,
    component: ScholarshipMembersView,
  },
  { key: 'cancelled', labelKey: 'memberViews.views.cancelled', available: false },
  { key: 'future-cancel', labelKey: 'memberViews.views.futureCancel', available: false },
  {
    key: 'outreach',
    labelKey: 'memberViews.views.outreach',
    available: true,
    component: RecommendedOutreachMembersView,
  },
  {
    key: 'renewals',
    labelKey: 'memberViews.views.renewals',
    available: true,
    component: UpcomingRenewalMembersView,
  },
  { key: 'past-patients', labelKey: 'memberViews.views.pastPatients', available: false },
  { key: 'ippe', labelKey: 'memberViews.views.ippe', available: true, component: IppeCalculationMembersView },
];

/**
 * Whether the signed-in user may see this view at all: true unless the view declares an extra
 * permission they don't hold. Fails closed while abilities aren't known yet. Says nothing about
 * whether the view is built — that's `available`.
 */
export function canAccessView(view: ViewEntry, ability: AppAbility | null): boolean {
  return !view.access || (ability?.can(...view.access) ?? false);
}
